import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import PolicyTab from '../../../components/common/PolicyTab';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CONTENT_TYPES = [
  { key: 'short',        label: 'Short',         description: '2–3 focused paragraphs', badge: 'secondary' },
  { key: 'brief',        label: 'Brief',          description: 'Clear & concise reference', badge: 'primary' },
  { key: 'elaborate',    label: 'Elaborate',      description: 'Full deep dive with details', badge: 'dark' },
  { key: 'examples',     label: 'Examples',       description: 'Concrete examples first', badge: 'success' },
  { key: 'more_examples',label: 'More Examples',  description: '5–10 varied examples', badge: 'success' },
  { key: 'coding',       label: 'Coding',         description: 'Code with line-by-line explanations', badge: 'info' },
  { key: 'realtime',     label: 'Real-time',      description: 'Industry production use cases', badge: 'warning' },
];

const ADDITIONAL_OPTIONS = [
  { key: 'include_explanation', label: 'Include Explanation' },
  { key: 'include_examples',    label: 'Include Examples' },
  { key: 'include_coding',      label: 'Include Coding' },
  { key: 'include_realtime',    label: 'Include Real-time Use Cases' },
];

const BADGE_COLOR = {
  short: 'secondary', brief: 'primary', elaborate: 'dark',
  examples: 'success', more_examples: 'success', coding: 'info', realtime: 'warning',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const creatorName = (c) => c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : 'Unknown';

// ─── BSModal ─────────────────────────────────────────────────────────────────

const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div
      className="modal fade show d-block"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── ContentRenderer ──────────────────────────────────────────────────────────

const ContentRenderer = ({ html }) => (
  <div
    dangerouslySetInnerHTML={{ __html: html }}
    style={{ lineHeight: 1.7 }}
    className="content-renderer"
  />
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ContentGeneratorPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');

  // Generate tab state
  const [form, setForm] = useState({
    courseName: '',
    moduleName: '',
    topicName: '',
    contentType: 'brief',
    additionalOptions: [],
    userPrompt: '',
  });
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saving, setSaving] = useState(false);

  // Library tab state
  const [records, setRecords] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libSearch, setLibSearch] = useState('');
  const [libContentType, setLibContentType] = useState('');
  const [viewItem, setViewItem] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 4000);
  };

  // ── Generate tab handlers ────────────────────────────────────────────────────

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleOption = (key) => {
    setForm(prev => ({
      ...prev,
      additionalOptions: prev.additionalOptions.includes(key)
        ? prev.additionalOptions.filter(k => k !== key)
        : [...prev.additionalOptions, key],
    }));
  };

  const handleGenerate = async () => {
    if (!form.courseName.trim()) { showToast('Course Name is required.', 'danger'); return; }
    if (!form.topicName.trim())  { showToast('Topic Name is required.', 'danger'); return; }

    setGenerating(true);
    setGeneratedContent('');
    try {
      const r = await api.post('/training/content-generator/generate', {
        courseName: form.courseName,
        moduleName: form.moduleName,
        topicName: form.topicName,
        contentType: form.contentType,
        additionalOptions: form.additionalOptions,
        userPrompt: form.userPrompt,
      });
      const html = r.data?.content || r.data?.data?.content || '';
      setGeneratedContent(html);
      if (!html) showToast('No content returned from AI.', 'warning');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to generate content.', 'danger');
    } finally {
      setGenerating(false);
    }
  };

  const openSaveDialog = () => {
    setSaveTitle(form.topicName || '');
    setShowSaveDialog(true);
  };

  const handleSave = async () => {
    if (!saveTitle.trim()) { showToast('Title is required.', 'danger'); return; }
    setSaving(true);
    try {
      await api.post('/training/content-generator', {
        title: saveTitle,
        courseName: form.courseName,
        moduleName: form.moduleName,
        topicName: form.topicName,
        contentType: form.contentType,
        additionalOptions: form.additionalOptions,
        userPrompt: form.userPrompt,
        generatedContent,
      });
      setShowSaveDialog(false);
      showToast('Saved to library!', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setGeneratedContent('');
  };

  // ── Library tab handlers ─────────────────────────────────────────────────────

  const fetchLibrary = useCallback(async () => {
    setLibLoading(true);
    try {
      const params = {};
      if (libSearch)      params.search      = libSearch;
      if (libContentType) params.contentType = libContentType;
      const r = await api.get('/training/content-generator', { params });
      const items = r.data?.records || r.data?.data?.items || r.data?.items || [];
      setRecords(items);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load library.', 'danger');
    } finally {
      setLibLoading(false);
    }
  }, [libSearch, libContentType]);

  useEffect(() => {
    if (activeTab === 'library') fetchLibrary();
  }, [activeTab, fetchLibrary]);

  const handleView = async (item) => {
    setShowViewDialog(true);
    setViewItem(item);
    setViewLoading(true);
    try {
      const r = await api.get(`/training/content-generator/${item._id}`);
      const full = r.data?.data || r.data;
      setViewItem(full);
    } catch (err) {
      showToast('Failed to load content.', 'danger');
    } finally {
      setViewLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record from the library?')) return;
    try {
      await api.delete(`/training/content-generator/${id}`);
      setRecords(prev => prev.filter(r => r._id !== id));
      showToast('Deleted.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete.', 'danger');
    }
  };

  const isOwnerOrAdmin = (item) => {
    const roleKey = user?.roleKey;
    const isAdminOrMgr = ['admin', 'manager'].includes(roleKey) || user?.userType === 'super_admin';
    const isOwner = item.createdBy && (
      item.createdBy._id === user?._id ||
      item.createdBy._id?.toString() === user?._id?.toString() ||
      item.createdBy === user?._id
    );
    return isAdminOrMgr || isOwner;
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="container-fluid py-3">
      <PageHeader
        title="AI Content Generator"
        subtitle="Generate and save AI-powered training content"
        icon="bi bi-stars"
      />

      {toast.show && (
        <AlertToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(t => ({ ...t, show: false }))}
        />
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'generate' ? 'active' : ''}`}
            onClick={() => setActiveTab('generate')}
          >
            <i className="bi bi-magic me-1" />Generate
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'library' ? 'active' : ''}`}
            onClick={() => setActiveTab('library')}
          >
            <i className="bi bi-collection me-1" />Library
            {records.length > 0 && (
              <span className="badge bg-secondary ms-1">{records.length}</span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'policy' ? 'active' : ''}`}
            onClick={() => setActiveTab('policy')}
          >
            <i className="bi bi-journal-text me-1" />Policy / Manual
          </button>
        </li>
      </ul>

      {activeTab === 'policy' && (
        <PolicyTab moduleKey="content-generator" label="AI Content Generator Policy / Manual" />
      )}

      {/* ── Generate Tab ── */}
      {activeTab === 'generate' && (
        <div className="row g-3">
          {/* Form Column */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white fw-semibold">
                <i className="bi bi-sliders me-2 text-primary" />Generation Settings
              </div>
              <div className="card-body">
                {/* Course Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Course Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Python Programming"
                    value={form.courseName}
                    onChange={e => handleFormChange('courseName', e.target.value)}
                  />
                </div>

                {/* Module Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Module Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Data Structures (optional)"
                    value={form.moduleName}
                    onChange={e => handleFormChange('moduleName', e.target.value)}
                  />
                </div>

                {/* Topic Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Topic Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Linked Lists"
                    value={form.topicName}
                    onChange={e => handleFormChange('topicName', e.target.value)}
                  />
                </div>

                {/* Content Type */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Content Type</label>
                  <div className="row g-2">
                    {CONTENT_TYPES.map(ct => (
                      <div key={ct.key} className="col-12">
                        <div
                          className={`card cursor-pointer border-2 ${form.contentType === ct.key ? `border-${ct.badge} bg-light` : 'border-light'}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleFormChange('contentType', ct.key)}
                        >
                          <div className="card-body py-2 px-3 d-flex align-items-center gap-2">
                            <input
                              type="radio"
                              className="form-check-input mt-0"
                              checked={form.contentType === ct.key}
                              onChange={() => handleFormChange('contentType', ct.key)}
                            />
                            <div>
                              <span className={`badge bg-${ct.badge} me-1`}>{ct.label}</span>
                              <small className="text-muted">{ct.description}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Options */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Additional Focus Areas</label>
                  {ADDITIONAL_OPTIONS.map(opt => (
                    <div key={opt.key} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`opt-${opt.key}`}
                        checked={form.additionalOptions.includes(opt.key)}
                        onChange={() => toggleOption(opt.key)}
                      />
                      <label className="form-check-label" htmlFor={`opt-${opt.key}`}>
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </div>

                {/* Additional Instructions */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Additional Instructions</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Any specific instructions for the AI... (optional)"
                    value={form.userPrompt}
                    onChange={e => handleFormChange('userPrompt', e.target.value)}
                  />
                </div>

                {/* Generate Button */}
                <button
                  className="btn btn-primary w-100"
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Generating content with AI...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-stars me-2" />Generate Content
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Result Column */}
          <div className="col-lg-8">
            {!generatedContent && !generating && (
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex flex-column align-items-center justify-content-center text-muted py-5">
                  <i className="bi bi-stars display-3 mb-3 text-primary opacity-25" />
                  <h5>AI Content will appear here</h5>
                  <p className="small">Fill in the form and click Generate Content to get started.</p>
                </div>
              </div>
            )}

            {generating && (
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex flex-column align-items-center justify-content-center py-5">
                  <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
                  <h5 className="text-primary">Generating content with AI...</h5>
                  <p className="text-muted small">This may take up to 30 seconds.</p>
                </div>
              </div>
            )}

            {generatedContent && !generating && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white d-flex align-items-center justify-content-between">
                  <div className="fw-semibold">
                    <i className="bi bi-file-earmark-richtext me-2 text-success" />
                    Generated Content
                    {form.topicName && (
                      <span className="text-muted fw-normal ms-2">— {form.topicName}</span>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-success" onClick={openSaveDialog}>
                      <i className="bi bi-bookmark-plus me-1" />Save to Library
                    </button>
                    <button className="btn btn-sm btn-outline-primary" onClick={handleGenerate}>
                      <i className="bi bi-arrow-repeat me-1" />Regenerate
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={handleClear}>
                      <i className="bi bi-x-lg me-1" />Clear
                    </button>
                  </div>
                </div>
                <div className="card-body p-4">
                  <style>{`
                    .content-renderer h2 { font-size: 1.3rem; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #1e293b; }
                    .content-renderer h3 { font-size: 1.1rem; font-weight: 600; margin-top: 1.2rem; margin-bottom: 0.4rem; color: #334155; }
                    .content-renderer p  { margin-bottom: 0.85rem; }
                    .content-renderer ul, .content-renderer ol { padding-left: 1.5rem; margin-bottom: 0.85rem; }
                    .content-renderer li { margin-bottom: 0.3rem; }
                    .content-renderer pre { background: #f1f5f9; border-radius: 6px; padding: 1rem; overflow-x: auto; margin-bottom: 1rem; }
                    .content-renderer code { font-family: 'Courier New', Courier, monospace; font-size: 0.88em; background: #f1f5f9; padding: 0.15em 0.4em; border-radius: 3px; }
                    .content-renderer pre code { background: none; padding: 0; }
                    .content-renderer strong { color: #0f172a; }
                  `}</style>
                  <ContentRenderer html={generatedContent} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Library Tab ── */}
      {activeTab === 'library' && (
        <div>
          {/* Filters */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-end">
                <div className="col-md-5">
                  <label className="form-label small fw-semibold mb-1">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search title, course, topic..."
                    value={libSearch}
                    onChange={e => setLibSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && fetchLibrary()}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-semibold mb-1">Content Type</label>
                  <select
                    className="form-select"
                    value={libContentType}
                    onChange={e => setLibContentType(e.target.value)}
                  >
                    <option value="">All Types</option>
                    {CONTENT_TYPES.map(ct => (
                      <option key={ct.key} value={ct.key}>{ct.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" onClick={fetchLibrary} disabled={libLoading}>
                    {libLoading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-arrow-clockwise me-1" />Refresh</>
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Records */}
          {libLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" />
              <p className="mt-2 text-muted">Loading library...</p>
            </div>
          ) : records.length === 0 ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5 text-muted">
                <i className="bi bi-collection display-4 mb-3 opacity-25" />
                <h5>No saved content yet</h5>
                <p className="small">Generate content and save it to the library to see it here.</p>
              </div>
            </div>
          ) : (
            <div className="row g-3">
              {records.map(item => (
                <div key={item._id} className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body d-flex align-items-start gap-3">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h6 className="mb-0 fw-semibold">{item.title}</h6>
                          <span className={`badge bg-${BADGE_COLOR[item.contentType] || 'secondary'}`}>
                            {CONTENT_TYPES.find(c => c.key === item.contentType)?.label || item.contentType}
                          </span>
                        </div>
                        <div className="text-muted small mb-1">
                          <i className="bi bi-journals me-1" />
                          {item.courseName}
                          {item.moduleName && <> <i className="bi bi-chevron-right mx-1" />{item.moduleName}</>}
                          {item.topicName  && <> <i className="bi bi-chevron-right mx-1" />{item.topicName}</>}
                        </div>
                        <div className="text-muted small">
                          <i className="bi bi-person me-1" />{creatorName(item.createdBy)}
                          <span className="mx-2">·</span>
                          <i className="bi bi-calendar me-1" />{fmtDate(item.createdAt)}
                        </div>
                      </div>
                      <div className="d-flex gap-2 flex-shrink-0">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleView(item)}
                        >
                          <i className="bi bi-eye me-1" />View
                        </button>
                        {isOwnerOrAdmin(item) && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(item._id)}
                          >
                            <i className="bi bi-trash me-1" />Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Save Dialog ── */}
      <BSModal
        show={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        title="Save to Library"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowSaveDialog(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : 'Save'}
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label fw-semibold">Title <span className="text-danger">*</span></label>
          <input
            type="text"
            className="form-control"
            value={saveTitle}
            onChange={e => setSaveTitle(e.target.value)}
            placeholder="Enter a title for this content..."
            autoFocus
          />
        </div>
        <div className="text-muted small">
          This will save the generated content along with course, module, and topic details to your library.
        </div>
      </BSModal>

      {/* ── View Dialog ── */}
      <BSModal
        show={showViewDialog}
        onClose={() => { setShowViewDialog(false); setViewItem(null); }}
        title={viewItem?.title || 'View Content'}
        size="modal-xl"
        footer={
          <button className="btn btn-secondary" onClick={() => { setShowViewDialog(false); setViewItem(null); }}>
            Close
          </button>
        }
      >
        {viewLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" />
            <p className="mt-2 text-muted">Loading content...</p>
          </div>
        ) : viewItem ? (
          <>
            <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
              <span className={`badge bg-${BADGE_COLOR[viewItem.contentType] || 'secondary'}`}>
                {CONTENT_TYPES.find(c => c.key === viewItem.contentType)?.label || viewItem.contentType}
              </span>
              <span className="text-muted small">
                <i className="bi bi-journals me-1" />
                {viewItem.courseName}
                {viewItem.moduleName && <> <i className="bi bi-chevron-right mx-1" />{viewItem.moduleName}</>}
                {viewItem.topicName  && <> <i className="bi bi-chevron-right mx-1" />{viewItem.topicName}</>}
              </span>
            </div>
            <div className="border rounded p-3" style={{ background: '#fafbfc' }}>
              <style>{`
                .content-renderer h2 { font-size: 1.3rem; font-weight: 700; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #1e293b; }
                .content-renderer h3 { font-size: 1.1rem; font-weight: 600; margin-top: 1.2rem; margin-bottom: 0.4rem; color: #334155; }
                .content-renderer p  { margin-bottom: 0.85rem; }
                .content-renderer ul, .content-renderer ol { padding-left: 1.5rem; margin-bottom: 0.85rem; }
                .content-renderer li { margin-bottom: 0.3rem; }
                .content-renderer pre { background: #f1f5f9; border-radius: 6px; padding: 1rem; overflow-x: auto; margin-bottom: 1rem; }
                .content-renderer code { font-family: 'Courier New', Courier, monospace; font-size: 0.88em; background: #f1f5f9; padding: 0.15em 0.4em; border-radius: 3px; }
                .content-renderer pre code { background: none; padding: 0; }
                .content-renderer strong { color: #0f172a; }
              `}</style>
              <ContentRenderer html={viewItem.generatedContent || '<p class="text-muted">No content available.</p>'} />
            </div>
          </>
        ) : null}
      </BSModal>
    </div>
  );
}
